import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth, usePermissions } from '../../hooks/useAuth';
import LoadingScreen from '../common/LoadingScreen';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string | string[];
  requiredPermission?: string;
  requiredFeature?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  requiredPermission,
  requiredFeature,
}) => {
  const { loading, user, token } = useAuth();
  const { isAuthenticated, hasRole, hasPermission, hasFeature, isApprovedAgent } = usePermissions();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen message="Checking authentication..." />;
  }

  // STRICT AUTHENTICATION CHECK - User must be explicitly authenticated
  if (!isAuthenticated || !user || !token) {
    // Redirect to login page with return URL
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role-based access
  if (requiredRole && !hasRole(requiredRole)) {
    return (
      <Navigate
        to="/dashboard"
        replace
      />
    );
  }

  // Check permission-based access
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return (
      <Navigate
        to="/dashboard"
        replace
      />
    );
  }

  // Check feature-based access
  if (requiredFeature && !hasFeature(requiredFeature)) {
    return (
      <Navigate
        to="/dashboard"
        replace
      />
    );
  }

  // Additional check for agent role - must be approved
  if (requiredRole && (requiredRole === 'agent' || (Array.isArray(requiredRole) && requiredRole.includes('agent'))) && !isApprovedAgent()) {
    return (
      <Navigate
        to="/dashboard"
        replace
      />
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
